import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import { BUSINESS_ADDRESS_LINES } from "@/lib/seo-constants";
import { COMPANY } from "@/lib/constants";
import { TRACKS } from "@/lib/tracks";
import { getTrackQuoteConfig } from "@/lib/quote-options";
import { localBusinessSchema, breadcrumbListSchema } from "@/lib/jsonld";

const ContactItem = ({ icon: Icon, label, value, href }) => (
    <li className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-brand-coral/10 text-brand-coral">
            <Icon className="h-4 w-4" strokeWidth={2.25} />
        </span>
        <div>
            <p className="text-[10px] uppercase tracking-wider text-brand-cyan font-semibold">
                {label}
            </p>
            {href ? (
                <a
                    href={href}
                    className="text-sm font-semibold text-white hover:text-brand-cyan transition-colors"
                >
                    {value}
                </a>
            ) : (
                <p className="text-sm font-semibold text-white">{value}</p>
            )}
        </div>
    </li>
);

const TrackContact = ({ trackKey, meta, headline, subhead }) => {
    const track = TRACKS[trackKey];
    const quoteConfig = getTrackQuoteConfig(trackKey);
    const validIds = useMemo(
        () => new Set(quoteConfig.serviceOptions.map((s) => s.id)),
        [quoteConfig.serviceOptions],
    );
    const [searchParams] = useSearchParams();

    const { initialServices, initialCity, utmSource, utmContent } = useMemo(() => {
        const svcParam = searchParams.get("service");
        const cityParam = searchParams.get("city");
        const services =
            svcParam && validIds.has(svcParam.toLowerCase())
                ? [svcParam.toLowerCase()]
                : [];
        return {
            initialServices: services,
            initialCity: cityParam ? cityParam.trim() : "",
            utmSource: (searchParams.get("utm_source") || "").trim().slice(0, 80),
            utmContent: (searchParams.get("utm_content") || "").trim().slice(0, 200),
        };
    }, [searchParams, validIds]);

    return (
        <>
            <Seo
                {...meta}
                path={track.contact}
                jsonLd={[
                    localBusinessSchema(),
                    breadcrumbListSchema([
                        { name: track.label, path: track.home },
                        { name: "Contact", path: track.contact },
                    ]),
                ]}
            />
            <Section dark className="py-20 md:py-24" data-testid="trackcontact-hero">
                <Container>
                    <div className="max-w-3xl">
                        <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                            {track.label} · Get a Quote
                        </p>
                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-none text-white"
                            data-testid="trackcontact-title"
                        >
                            {headline}
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-white/75 font-light italic leading-relaxed max-w-2xl">
                            {subhead}
                        </p>
                    </div>
                </Container>
            </Section>

            <Section className="py-14 md:py-20" data-testid="trackcontact-body">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
                        <div className="lg:col-span-8">
                            <QuoteForm
                                sourcePage={track.contact}
                                track={trackKey}
                                serviceOptions={quoteConfig.serviceOptions}
                                propertyTypes={quoteConfig.propertyTypes}
                                isB2B={quoteConfig.isB2B}
                                initialServices={initialServices}
                                initialCity={initialCity}
                                utmSource={utmSource}
                                utmContent={utmContent}
                            />
                        </div>

                        <aside className="lg:col-span-4">
                            <div className="bg-brand-dark text-white p-7 md:p-8" data-testid="trackcontact-sidebar">
                                <h3 className="font-display text-2xl tracking-wide text-white mb-5">
                                    Prefer to talk?
                                </h3>
                                <ul className="space-y-4">
                                    <ContactItem icon={Phone} label="Phone" value={COMPANY.phone} href={COMPANY.phoneHref} />
                                    <ContactItem icon={Mail} label="Email" value={COMPANY.email} href={COMPANY.emailHref} />
                                    <ContactItem
                                        icon={MapPin}
                                        label="Address"
                                        value={BUSINESS_ADDRESS_LINES.map((line) => (
                                            <span key={line} className="block">
                                                {line}
                                            </span>
                                        ))}
                                    />
                                    <ContactItem icon={Clock} label="Hours" value={COMPANY.hours} />
                                </ul>
                                <div className="mt-7 pt-6 border-t border-white/10">
                                    <p className="text-xs text-white/60 leading-relaxed">
                                        Licensed &amp; insured. COIs and WSIB clearance
                                        provided on request. We respond within one business
                                        day.
                                    </p>
                                </div>
                            </div>
                        </aside>
                    </div>
                </Container>
            </Section>
        </>
    );
};

export default TrackContact;
